import { cn } from "@d12labs/utils";
import type { LucideIcon } from "lucide-react";
import type * as React from "react";

/**
 * The one page-header pattern, used by every plugin and core page so headers
 * stop drifting. Anatomy (left → right, top → bottom):
 *
 *   [eyebrow]                                    [kbdHint] [actions]
 *   [icon] Title  [badges]
 *          Subtitle
 *   [tab] [tab] [tab]                            ← optional, hairline below
 *
 * Actions render in source order; by convention pass them ghost → secondary →
 * primary (left → right). A hairline always closes the header.
 *
 * Pair with <PageBody> for the scrollable content region. The app shell's <main>
 * is a flush flex column, so a page is typically:
 *
 *   <PageHeader … />
 *   <PageBody className="p-6 …">…</PageBody>
 */
export function PageHeader({
	eyebrow,
	icon: Icon,
	title,
	subtitle,
	badges,
	actions,
	tabs,
	kbdHint,
	className,
}: {
	eyebrow?: React.ReactNode;
	icon?: LucideIcon;
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	/** Inline next to the title — counts, status badges. */
	badges?: React.ReactNode;
	/** ghost → secondary → primary, right-aligned. */
	actions?: React.ReactNode;
	/** Optional <PageTab> row below the title. */
	tabs?: React.ReactNode;
	kbdHint?: React.ReactNode;
	className?: string;
}) {
	return (
		<header
			data-slot="page-header"
			className={cn("flex flex-none flex-col gap-3 border-b border-hairline", className)}
		>
			<div className="flex items-start justify-between gap-4 px-6 pt-5 pb-1">
				<div className="flex min-w-0 items-start gap-3">
					{Icon ? (
						<span className="mt-1 flex size-7 flex-none items-center justify-center rounded-md bg-surface-2 text-ink-muted">
							<Icon className="size-4" />
						</span>
					) : null}
					<div className="min-w-0">
						{eyebrow ? (
							<div className="text-eyebrow uppercase text-ink-subtle">{eyebrow}</div>
						) : null}
						<div className="flex items-center gap-2">
							<h1 className="headline truncate text-foreground">{title}</h1>
							{badges}
						</div>
						{subtitle ? (
							<p className="mt-1 max-w-2xl text-body-sm text-ink-subtle">{subtitle}</p>
						) : null}
					</div>
				</div>
				{actions || kbdHint ? (
					<div className="flex flex-none items-center gap-2 pt-1">
						{kbdHint ? <span className="text-meta text-ink-subtle">{kbdHint}</span> : null}
						{actions}
					</div>
				) : null}
			</div>
			{tabs ? <nav className="flex items-center gap-5 px-6">{tabs}</nav> : null}
		</header>
	);
}

/**
 * A page-header tab. Underline-active, hairline divider closes the header row.
 * Render as a button (default) or pass `render` to wrap in a router <Link>.
 */
export function PageTab({
	active = false,
	count,
	children,
	render,
	...props
}: React.ComponentProps<"button"> & {
	active?: boolean;
	count?: number;
	render?: (props: { className: string; children: React.ReactNode }) => React.ReactNode;
}) {
	const className = cn(
		"-mb-px border-b-2 py-2 text-body-sm transition-colors",
		active
			? "border-primary text-foreground"
			: "border-transparent text-ink-subtle hover:text-foreground",
	);
	const inner = (
		<>
			{children}
			{count != null ? (
				<span className="ml-1.5 text-meta tabular-nums text-ink-subtle">{count}</span>
			) : null}
		</>
	);
	if (render) return <>{render({ className, children: inner })}</>;
	return (
		<button type="button" className={className} {...props}>
			{inner}
		</button>
	);
}

/**
 * Scrollable content region beneath a <PageHeader>. Fills remaining height and
 * scrolls on its own. Pass padding/layout via `className` (e.g. "p-6 space-y-6"
 * for dashboards, or wrap an inner `mx-auto max-w-2xl` for form/reader pages).
 */
export function PageBody({
	className,
	children,
}: {
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<div
			data-slot="page-body"
			className={cn(
				"scrollbar-gutter-stable scrollbar-thin scrollbar-thumb-hairline-strong scrollbar-track-transparent min-h-0 flex-1 overflow-y-auto",
				className,
			)}
		>
			{children}
		</div>
	);
}
